/**
 * useMediaSession — Integração com a Media Session API
 * ────────────────────────────────────────────────────
 *
 * Expõe controlos de mídia ao SO (lock screen do telemóvel, Control Center
 * em iOS, popup do CarPlay/Android Auto, botões de teclado dedicados) e
 * mostra metadata (título / artista / artwork) enquanto a faixa toca.
 *
 * Uso típico em <audio>/<video>:
 *
 *     const audioRef = useRef(null);
 *     useMediaSession(audioRef, {
 *       title: item.Descricao,
 *       artist: "IHTP Media",
 *       artwork: [{ src: item.ThumbnailUrl, sizes: "512x512" }],
 *     });
 *
 * Suporta `play / pause / seekbackward / seekforward / previoustrack /
 * nexttrack`. Os dois últimos só são registados se o caller passar
 * `onPrev`/`onNext`. Mantém sempre o `playbackState` em sincronia com o
 * `<audio>` e actualiza `positionState` cada 250ms para a barra de
 * progresso no lock-screen.
 *
 * Compatibilidade: Chrome/Edge/Safari (iOS 15+) e Firefox 122+.
 * Em browsers sem suporte, o hook é um no-op silencioso.
 */
import { useEffect } from "react";

const SEEK_STEP_SECONDS = 10;

export default function useMediaSession(
  mediaRef,
  { title, artist = "IHTP Media", album, artwork = [], onPrev, onNext } = {},
) {
  useEffect(() => {
    const el = mediaRef?.current;
    if (!el) return;
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      return;
    }
    const ms = navigator.mediaSession;

    // ─── Metadata ──────────────────────────────────────────────────
    try {
      ms.metadata = new window.MediaMetadata({
        title: title || "",
        artist,
        album: album || "IHTP Media Center",
        artwork: (artwork || []).filter((a) => a && a.src),
      });
    } catch (e) {
      // older Safari versions throw on partial metadata — soft-fail
      // eslint-disable-next-line no-console
      console.warn("MediaSession metadata failed", e);
    }

    // ─── Action handlers ──────────────────────────────────────────
    const setHandler = (action, handler) => {
      try { ms.setActionHandler(action, handler); }
      catch { /* unsupported action */ }
    };

    setHandler("play", () => { el.play().catch(() => {}); });
    setHandler("pause", () => { el.pause(); });
    setHandler("seekbackward", (details) => {
      const skip = details?.seekOffset || SEEK_STEP_SECONDS;
      el.currentTime = Math.max(0, (el.currentTime || 0) - skip);
    });
    setHandler("seekforward", (details) => {
      const skip = details?.seekOffset || SEEK_STEP_SECONDS;
      el.currentTime = Math.min(el.duration || Infinity, (el.currentTime || 0) + skip);
    });
    setHandler("seekto", (details) => {
      if (details?.seekTime != null) {
        el.currentTime = details.seekTime;
      }
    });
    setHandler("stop", () => { el.pause(); el.currentTime = 0; });
    setHandler("previoustrack", onPrev || null);
    setHandler("nexttrack", onNext || null);

    // ─── Playback state sync ──────────────────────────────────────
    const onPlay = () => { ms.playbackState = "playing"; };
    const onPause = () => { ms.playbackState = "paused"; };
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onPause);

    // Estado inicial coerente com o elemento
    ms.playbackState = el.paused ? "paused" : "playing";

    // ─── Position state (lock-screen progress bar) ────────────────
    let posTimer = null;
    const updatePos = () => {
      const dur = el.duration;
      const cur = el.currentTime || 0;
      if (Number.isFinite(dur) && dur > 0 && typeof ms.setPositionState === "function") {
        try {
          ms.setPositionState({
            duration: dur,
            playbackRate: el.playbackRate || 1,
            position: Math.min(cur, dur),
          });
        } catch { /* invalid state (e.g. duration NaN) — ignore */ }
      }
    };
    posTimer = window.setInterval(updatePos, 250);
    el.addEventListener("loadedmetadata", updatePos);

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onPause);
      el.removeEventListener("loadedmetadata", updatePos);
      if (posTimer) window.clearInterval(posTimer);
      // Limpa handlers (importante: outras páginas podem partilhar a Session)
      ["play", "pause", "seekbackward", "seekforward", "seekto",
        "stop", "previoustrack", "nexttrack"].forEach((a) => {
        try { ms.setActionHandler(a, null); }
        catch { /* ignore */ }
      });
      try { ms.metadata = null; }
      catch { /* ignore */ }
    };
    // Re-run when metadata or callbacks change.
  }, [mediaRef, title, artist, album, JSON.stringify(artwork), onPrev, onNext]);
}
